(() => {
  "use strict";

  // ========= GLSL =========
  const VERT_SRC = `
precision mediump float;
varying vec2 vUv;
attribute vec2 a_position;

void main() {
  vUv = 0.5 * (a_position + 1.0);
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

  const FRAG_SRC = `
precision mediump float;

varying vec2 vUv;
uniform sampler2D u_image_texture;
uniform float u_time;
uniform float u_ratio;
uniform float u_img_ratio;
uniform float u_blueish;
uniform float u_scale;
uniform float u_illumination;
uniform float u_surface_distortion;
uniform float u_water_distortion;
uniform float u_opacity;

vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permute(vec3 x) { return mod289(((x*34.0)+1.0)*x); }

float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
  vec2 i = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1;
  i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod289(i);
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
  m = m*m;
  m = m*m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0*a0 + h*h);
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

mat2 rotate2D(float r) {
  return mat2(cos(r), sin(r), -sin(r), cos(r));
}

float surface_noise(vec2 uv, float t, float scale) {
  vec2 n = vec2(0.1);
  vec2 N = vec2(0.1);
  mat2 m = rotate2D(0.5);
  for (int j = 0; j < 10; j++) {
    uv *= m;
    n *= m;
    vec2 q = uv * scale + float(j) + n + (0.5 + 0.5 * float(j)) * (mod(float(j), 2.0) - 1.0) * t;
    n += sin(q);
    N += cos(q) / scale;
    scale *= 1.2;
  }
  return (N.x + N.y + 0.1);
}

void main() {
  vec2 uv = vUv;
  uv.y = 1.0 - uv.y;
  uv.x *= u_ratio;

  float t = 0.002 * u_time;
  vec3 color = vec3(0.0);

  float outer_noise = snoise((0.3 + 0.1 * sin(t)) * uv + vec2(0.0, 0.2 * t));
  vec2 surface_noise_uv = 2.0 * uv + (outer_noise * 0.2);

  float sNoise = surface_noise(surface_noise_uv, t, u_scale);
  sNoise *= pow(uv.y, 0.3);
  sNoise = pow(sNoise, 2.0);

  vec2 img_uv = vUv;
  img_uv -= 0.5;
  img_uv *= 1.4;
  img_uv += 0.5;
  img_uv.y = 1.0 - img_uv.y;

  img_uv += (u_water_distortion * outer_noise);
  img_uv += (u_surface_distortion * sNoise);

  vec3 bg_color = vec3(0.0, 0.0, 0.0);
  vec2 pattern_uv = img_uv * 4.0;
  float pattern = sin(pattern_uv.x * 3.14159) * sin(pattern_uv.y * 3.14159);
  pattern = smoothstep(0.0, 0.5, pattern);

  vec3 img_color = mix(bg_color * 0.5, bg_color, pattern);
  img_color *= (1.0 + u_illumination * sNoise);

  color += img_color;
  color += u_illumination * vec3(1.0 - u_blueish, 1.0, 1.0) * sNoise;

  gl_FragColor = vec4(color, u_opacity);
}
`;

  // ========= Defaults via CSS vars =========
  const CSS = {
    opacity: "--water-opacity",
    water: "--water-distortion",
    illum: "--illumination",
    blue: "--blueish",
    scale: "--scale",
    surface: "--surface-distortion",
  };

  const DEF = {
    opacity: 0.35,
    water: 0.00672,
    illum: 0.15,
    blue: 0.6,
    scale: 7,
    surface: 0,
  };

  const DPR = () => Math.min(window.devicePixelRatio || 1, 2);

  function cssNum(el, name, fallback) {
    const raw = getComputedStyle(el).getPropertyValue(name).trim();
    if (!raw) return fallback;
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
  }

  function compile(gl, type, src) {
    const sh = gl.createShader(type);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      console.error("WaterFX shader error:", gl.getShaderInfoLog(sh));
      gl.deleteShader(sh);
      return null;
    }
    return sh;
  }

  function link(gl, vs, fs) {
    const p = gl.createProgram();
    gl.attachShader(p, vs);
    gl.attachShader(p, fs);
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      console.error("WaterFX link error:", gl.getProgramInfoLog(p));
      gl.deleteProgram(p);
      return null;
    }
    gl.useProgram(p);
    return p;
  }

  function getUniforms(gl, program) {
    const out = {};
    const n = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);
    for (let i = 0; i < n; i++) {
      const name = gl.getActiveUniform(program, i).name;
      out[name] = gl.getUniformLocation(program, name);
    }
    return out;
  }

  function setupQuad(gl, program) {
    const vertices = new Float32Array([-1, -1,  1, -1,  -1,  1,  1,  1]);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

    const aPos = gl.getAttribLocation(program, "a_position");
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);
  }

  function blankDataUrl(w, h) {
    const c = document.createElement("canvas");
    c.width = Math.max(1, Math.floor(w));
    c.height = Math.max(1, Math.floor(h));
    return c.toDataURL("image/png");
  }

  function initOne(container) {
    if (container.__waterfx) return;

    const canvas = container.querySelector("canvas.waterfx-canvas");
    if (!canvas) return;

    const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) {
      console.warn("WaterFX: WebGL not available for", container);
      return;
    }

    const vs = compile(gl, gl.VERTEX_SHADER, VERT_SRC);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG_SRC);
    if (!vs || !fs) return;

    const program = link(gl, vs, fs);
    if (!program) return;

    const u = getUniforms(gl, program);
    setupQuad(gl, program);

    // Texture (wie Original: leeres Bild)
    const img = new Image();
    img.crossOrigin = "anonymous";
    let texReady = false;

    img.onload = () => {
      const tex = gl.createTexture();
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
      gl.uniform1i(u.u_image_texture, 0);
      texReady = true;
      resize();
    };

    // initial seed
    {
      const r0 = container.getBoundingClientRect();
      img.src = blankDataUrl(r0.width, r0.height);
    }

    let alive = true;
    let rafId = 0;

    const resize = () => {
      if (!alive) return;
      const r = container.getBoundingClientRect();
      const s = DPR();
      const w = Math.max(1, Math.floor(r.width * s));
      const h = Math.max(1, Math.floor(r.height * s));

      if (canvas.width !== w) canvas.width = w;
      if (canvas.height !== h) canvas.height = h;

      gl.viewport(0, 0, w, h);
      gl.uniform1f(u.u_ratio, w / h);

      const ir = img.naturalHeight ? (img.naturalWidth / img.naturalHeight) : 1.0;
      gl.uniform1f(u.u_img_ratio, ir);
    };

    const render = (t) => {
      if (!alive) return;

      if (texReady) {
        gl.uniform1f(u.u_time, t);

        gl.uniform1f(u.u_blueish, cssNum(container, CSS.blue, DEF.blue));
        gl.uniform1f(u.u_scale, cssNum(container, CSS.scale, DEF.scale));
        gl.uniform1f(u.u_illumination, cssNum(container, CSS.illum, DEF.illum));
        gl.uniform1f(u.u_surface_distortion, cssNum(container, CSS.surface, DEF.surface));
        gl.uniform1f(u.u_water_distortion, cssNum(container, CSS.water, DEF.water));
        gl.uniform1f(u.u_opacity, cssNum(container, CSS.opacity, DEF.opacity));

        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      }

      rafId = requestAnimationFrame(render);
    };

    let ro = null;
    if ("ResizeObserver" in window) {
      ro = new ResizeObserver(resize);
      ro.observe(container);
    } else {
      window.addEventListener("resize", resize);
    }

    container.__waterfx = {
      stop() {
        alive = false;
        if (rafId) cancelAnimationFrame(rafId);
        if (ro) ro.disconnect();
      },
      resize,
    };

    resize();
    rafId = requestAnimationFrame(render);
  }

  function boot() {
    document.querySelectorAll(".water-effect").forEach(initOne);
  }

  document.addEventListener("DOMContentLoaded", boot);

  // Shopify: Sections können nachgeladen werden
  const mo = new MutationObserver((muts) => {
    for (const m of muts) {
      for (const n of m.addedNodes) {
        if (n.nodeType !== 1) continue;
        if (n.matches?.(".water-effect")) initOne(n);
        n.querySelectorAll?.(".water-effect").forEach(initOne);
      }
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

})();